import { Service, DiscountCategory } from '@turizi/shared';
import { useTranslation } from 'react-i18next';
import { NumberInput, Badge, Text, Group, Popover, Button } from '@mantine/core';
import { IconPercentage, IconEdit, IconTrash, IconPlus } from '@tabler/icons-react';
import { useState, useEffect, useMemo } from 'react';
import { useUpdateService, useDiscountCategories } from '@/hooks/api-hooks';
import { SkeletonList } from './snippets';
import { notify } from '@/lib/notify';

interface EditServiceDiscountsProps {
    service: Service;
}

export default function EditServiceDiscounts({ service }: EditServiceDiscountsProps) {
    const { t } = useTranslation();
    const [discounts, setDiscounts] = useState<any[]>([]);
    const [editingCell, setEditingCell] = useState<{ category: string; currency: string } | null>(null);
    const [tempValue, setTempValue] = useState<number>(0);

    const updateServiceMutation = useUpdateService();
    const {
        data: discountCategories,
        isLoading: isLoadingDiscountCategories
    } = useDiscountCategories();

    const discountCategoriesData = useMemo(
        () => discountCategories?.data || [], [discountCategories]
    );

    useEffect(() => {
        // Initialize discounts with strictly what is configured on the service
        setDiscounts(service.discounts || []);
    }, [service.discounts]);

    const activeNames = useMemo(() => new Set(discounts.map(d => d.category)), [discounts]);

    const availableCategories = useMemo(() => {
        return discountCategoriesData.filter(cat => !activeNames.has(cat.category));
    }, [discountCategoriesData, activeNames]);

    const handleDiscountChange = (name: string, currency: string, percentage: number) => {
        const updatedDiscounts = discounts.map(discount =>
            discount.category === name
                ? {
                    ...discount,
                    discounts: (discount.discounts || []).map((d: any) =>
                        d.currency === currency
                            ? { ...d, percentage }
                            : d
                    )
                }
                : discount
        );
        setDiscounts(updatedDiscounts);

        updateServiceMutation.mutate({
            id: service.id,
            discounts: updatedDiscounts as any,
        }, {
            onSuccess: () => {
                notify.success(t('discount_update_success'), '');
            },
            onError: () => {
                notify.error(t('discount_update_error'), '');
            }
        });
    };

    const handleAddCategory = (category: DiscountCategory) => {
        const newDiscounts = [
            ...discounts,
            {
                category: category.category,
                discounts: category.discounts.map(d => ({
                    type: d.type || 'percentage',
                    category: d.category || category.category,
                    currency: d.currency,
                    percentage: d.percentage
                }))
            }
        ];
        setDiscounts(newDiscounts);

        updateServiceMutation.mutate({
            id: service.id,
            discounts: newDiscounts as any,
        }, {
            onSuccess: () => {
                notify.success(t('discount_update_success'), '');
            },
            onError: () => {
                notify.error(t('discount_update_error'), '');
            }
        });
    };

    const handleRemoveCategory = (categoryName: string) => {
        const newDiscounts = discounts.filter(d => d.category !== categoryName);
        setDiscounts(newDiscounts);

        updateServiceMutation.mutate({
            id: service.id,
            discounts: newDiscounts as any,
        }, {
            onSuccess: () => {
                notify.success(t('discount_update_success'), '');
            },
            onError: () => {
                notify.error(t('discount_update_error'), '');
            }
        });
    };

    const handleCellClick = (name: string, currency: string) => {
        const discount = discounts
            .find(d => d.category === name)
            ?.discounts?.find((d: any) => d.currency === currency);

        if (discount) {
            setEditingCell({ category: name, currency });
            setTempValue(discount.percentage);
        }
    };

    const handleSave = () => {
        if (editingCell) {
            handleDiscountChange(editingCell.category, editingCell.currency, tempValue);
            setEditingCell(null);
        }
    };

    const handleCancel = () => {
        setEditingCell(null);
    };

    const getDiscountValue = (name: string, currency: string) => {
        return discounts
            .find(d => d.category === name)
            ?.discounts?.find((d: any) => d.currency === currency)?.percentage || 0;
    };

    // Get unique currencies from all active and global categories
    const currencies = useMemo(() => {
        const activeCurrencies = new Set<string>();
        discounts.flatMap(category => category.discounts?.map((d: any) => d.currency) || []).forEach(c => activeCurrencies.add(c));
        discountCategoriesData.flatMap(category => category.discounts?.map((d: any) => d.currency) || []).forEach(c => activeCurrencies.add(c));
        return Array.from(activeCurrencies).sort();
    }, [discounts, discountCategoriesData]);

    const [isApplying, setIsApplying] = useState(false);

    const handleApplyDefaultDiscounts = () => {
        setIsApplying(true);
        const sanitizedDefaults = discountCategoriesData.map(category => ({
            category: category.category,
            discounts: category.discounts.map(d => ({
                type: d.type || 'percentage',
                category: d.category || category.category,
                currency: d.currency,
                percentage: d.percentage
            }))
        }));

        updateServiceMutation.mutate({
            id: service.id,
            discounts: sanitizedDefaults as any
        }, {
            onSettled: () => setIsApplying(false),
            onSuccess: () => {
                notify.success(t('discount_update_success'), '');
            },
            onError: () => {
                notify.error(t('discount_update_error'), '');
            }
        });
    };

    if (isLoadingDiscountCategories) {
        return <SkeletonList />
    }

    return (
        <div className='flex gap-4 w-full'>
            {/* Custom configured discounts section */}
            <div className='flex-1'>
                <div className="lex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6">
                    <div>
                        <div className="text-xl font-semibold text-gray-900">
                            {t('service_discounts', 'Service Custom Discounts')}
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                            {t('service_discounts_desc', 'Only these discounts are explicitly saved and applied to this service.')}
                        </div>
                    </div>
                    {discounts.length === 0 && !isApplying && (
                        <Button
                            variant="light"
                            color="black"
                            size="sm"
                            onClick={handleApplyDefaultDiscounts}
                        >
                            {t('apply_all_defaults', 'Apply All Tenant Defaults')}
                        </Button>
                    )}
                </div>

                {isApplying ? (
                    <SkeletonList />
                ) : discounts.length === 0 ? (
                    <div className="text-sm text-gray-500 py-8 text-center italic bg-gray-50/50 rounded-lg border border-dashed border-gray-200">
                        {t('no_discounts_configured', 'No custom discounts configured for this service yet. Defaulting to 0% discount.')}
                    </div>
                ) : (
                    <div className="subtle-table">
                        <table>
                            <tbody>
                                {discounts.map((category) => (
                                    <tr key={category.category}>
                                        <td>
                                            <div className='font-bold'>
                                                {category.category}
                                            </div>
                                        </td>
                                        {currencies.map((currency) => (
                                            <td key={`${category.category}-${currency}`} className='number'>
                                                <Popover
                                                    opened={editingCell?.category === category.category && editingCell?.currency === currency}
                                                    onChange={() => setEditingCell(null)}
                                                    position="bottom"
                                                    withArrow
                                                    shadow="lg"
                                                >
                                                    <Popover.Target>
                                                        <div
                                                            onClick={() => handleCellClick(category.category!, currency)}
                                                            className={`
                                                                flex justify-end gap-1 items-center p-2 rounded cursor-pointer
                                                                ${editingCell?.category === category.category && editingCell?.currency === currency ? 'bg-gray-200' : ''}
                                                            `}
                                                        >
                                                            <Text size="sm">
                                                                {getDiscountValue(category.category!, currency)}%
                                                            </Text>
                                                            <Text size="xs">
                                                                {currency}
                                                            </Text>
                                                        </div>
                                                    </Popover.Target>
                                                    <Popover.Dropdown>
                                                        <div className="p-2 space-y-3">
                                                            <NumberInput
                                                                value={tempValue}
                                                                onChange={(value) => setTempValue(Number(value) || 0)}
                                                                min={0}
                                                                max={100}
                                                                rightSection={<IconPercentage size={16} />}
                                                                size="sm"
                                                                w={120}
                                                            />
                                                            <Group gap="xs" justify="flex-end">
                                                                <Button
                                                                    size="xs"
                                                                    variant="subtle"
                                                                    color='black'
                                                                    onClick={handleCancel}
                                                                >
                                                                    Cancel
                                                                </Button>
                                                                <Button
                                                                    size="xs"
                                                                    radius='lg'
                                                                    color='black'
                                                                    onClick={handleSave}
                                                                >
                                                                    Save
                                                                </Button>
                                                            </Group>
                                                        </div>
                                                    </Popover.Dropdown>
                                                </Popover>
                                            </td>
                                        ))}
                                        <td>
                                            <Button
                                                variant="subtle"
                                                color="red"
                                                size="xs"
                                                onClick={() => handleRemoveCategory(category.category!)}
                                            >
                                                <IconTrash size={16} />
                                            </Button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Available, but unassigned tenant categories */}
            <div>
                <div className="mb-6">
                    <div className="text-lg font-semibold text-gray-800">
                        {t('available_tenant_categories', 'Available Tenant Categories')}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                        {t('available_tenant_categories_desc', 'These categories exist globally in Settings but are NOT active for this service.')}
                    </div>
                </div>

                {availableCategories.length === 0 ? (
                    <div className="text-sm text-gray-500 py-4 text-center italic bg-white rounded-lg border border-gray-100">
                        {t('all_categories_assigned', 'All tenant categories are configured for this service.')}
                    </div>
                ) : (
                    <div className="subtle-table bg-white rounded-lg border border-gray-200 overflow-hidden">
                        <table>
                            <thead>
                                <tr>
                                    <th>{t('category_name', 'Category Name')}</th>
                                    <th>{t('default_percentages', 'Default Percentages')}</th>
                                    <th className="w-24"></th>
                                </tr>
                            </thead>
                            <tbody>
                                {availableCategories.map((category) => (
                                    <tr key={category.category}>
                                        <td className="font-semibold text-sm">
                                            {category.category}
                                        </td>
                                        <td>
                                            <div className="flex gap-2">
                                                {category.discounts?.map((d: any, idx: number) => (
                                                    <Badge key={idx} variant="outline" color="gray">
                                                        {d.currency}: {d.percentage}%
                                                    </Badge>
                                                ))}
                                            </div>
                                        </td>
                                        <td>
                                            <Button
                                                variant="light"
                                                color="black"
                                                size="xs"
                                                leftSection={<IconPlus size={14} />}
                                                onClick={() => handleAddCategory(category)}
                                            >
                                                {t('assign', 'Assign')}
                                            </Button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}